import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrashIcon, KeyIcon, UserIcon, ShieldCheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { objectStorageService } from '../../services/objectStorageService';
import { copyToClipboard } from '../../utils/clipboard';
import type { BucketOperation, CreatedAccessKey } from '../../types/objectStorage';

export type AccessPreset = 'read-only' | 'read-write' | 'full' | 'custom';

export function presetToOps(preset: AccessPreset): BucketOperation[] {
  switch (preset) {
    case 'read-only':  return ['list', 'read'];
    case 'read-write': return ['list', 'read', 'write'];
    case 'full':       return ['list', 'read', 'write', 'delete'];
    case 'custom':     return [];
  }
}

interface Props {
  projectId: string;
  bucketId: string;
}

const ALL_OPS: BucketOperation[] = ['list', 'read', 'write', 'delete'];

export default function BucketAccessPanel({ projectId, bucketId }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'users' | 'roles'>('users');
  const [newName, setNewName] = useState('');
  const [preset, setPreset] = useState<AccessPreset>('read-write');
  const [newOps, setNewOps] = useState<Set<BucketOperation>>(new Set(presetToOps('read-write')));
  const [createdKey, setCreatedKey] = useState<CreatedAccessKey | null>(null);

  const usersQuery = useQuery({
    queryKey: ['iam-users', projectId, bucketId],
    queryFn: () => objectStorageService.listBucketUsers(projectId, bucketId),
    enabled: tab === 'users',
  });

  const rolesQuery = useQuery({
    queryKey: ['iam-roles', projectId, bucketId],
    queryFn: () => objectStorageService.listBucketRoles(projectId, bucketId),
    enabled: tab === 'roles',
  });

  const createUser = useMutation({
    mutationFn: () =>
      objectStorageService.createBucketUser(projectId, bucketId, newName, Array.from(newOps)),
    onSuccess: (data) => {
      setCreatedKey(data.access_key);
      setNewName('');
      qc.invalidateQueries({ queryKey: ['iam-users', projectId, bucketId] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Create user failed'),
  });

  const deleteUser = useMutation({
    mutationFn: (username: string) =>
      objectStorageService.deleteBucketUser(projectId, bucketId, username),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam-users', projectId, bucketId] });
      toast.success('User deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Delete failed'),
  });

  const createRole = useMutation({
    mutationFn: () =>
      objectStorageService.createBucketRole(projectId, bucketId, newName, Array.from(newOps)),
    onSuccess: () => {
      setNewName('');
      toast.success('Role created');
      qc.invalidateQueries({ queryKey: ['iam-roles', projectId, bucketId] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Create role failed'),
  });

  const deleteRole = useMutation({
    mutationFn: (roleName: string) =>
      objectStorageService.deleteBucketRole(projectId, bucketId, roleName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam-roles', projectId, bucketId] });
      toast.success('Role deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Delete failed'),
  });

  const toggleOp = (op: BucketOperation) => {
    const next = new Set(newOps);
    if (next.has(op)) next.delete(op);
    else next.add(op);
    setNewOps(next);
  };

  return (
    <div className="rounded bg-gray-900/60 border border-gray-700 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheckIcon className="w-4 h-4 text-primary-400" />
        <p className="text-sm font-medium text-white">Access</p>
        <div className="ml-auto flex gap-1 text-xs">
          <button
            onClick={() => setTab('users')}
            className={`px-2 py-1 rounded ${tab === 'users' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Users
          </button>
          <button
            onClick={() => setTab('roles')}
            className={`px-2 py-1 rounded ${tab === 'roles' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Roles
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={tab === 'users' ? 'username' : 'role name'}
            className="flex-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-white"
          />
          <button
            onClick={() => (tab === 'users' ? createUser.mutate() : createRole.mutate())}
            disabled={!newName || newOps.size === 0 || createUser.isPending || createRole.isPending}
            className="px-3 py-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs rounded"
          >
            Create
          </button>
        </div>
        <div className="flex gap-1.5 flex-wrap text-[11px]">
          {(['read-only', 'read-write', 'full', 'custom'] as AccessPreset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setPreset(p);
                if (p !== 'custom') setNewOps(new Set(presetToOps(p)));
              }}
              className={`px-2 py-1 rounded-full border ${
                preset === p
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'
              }`}
            >
              {p === 'read-only' ? 'Read-only' : p === 'read-write' ? 'Read + Write' : p === 'full' ? 'Full access' : 'Custom'}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex gap-2 text-xs text-gray-300">
            {ALL_OPS.map((op) => (
              <label key={op} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newOps.has(op)}
                  onChange={() => toggleOp(op)}
                  className="rounded"
                />
                {op}
              </label>
            ))}
          </div>
        )}
      </div>

      {tab === 'users' && (
        <div className="space-y-1">
          {usersQuery.isLoading && <p className="text-xs text-gray-400">Loading…</p>}
          {usersQuery.data?.map((u) => (
            <div key={u.id} className="flex items-center gap-2 py-1 text-xs">
              <UserIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-white font-mono flex-1 truncate">{u.username}</span>
              <span className="text-gray-500">{u.operations}</span>
              <button
                onClick={() => deleteUser.mutate(u.username)}
                className="p-1 text-gray-400 hover:text-red-400"
                title="Delete user"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {usersQuery.data?.length === 0 && newName === '' && (
            <div className="rounded border border-dashed border-gray-700 p-4 text-center text-xs text-gray-400">
              <p className="mb-2">No access keys yet.</p>
              <button
                type="button"
                onClick={() => {
                  setPreset('read-write');
                  setNewOps(new Set(presetToOps('read-write')));
                  setNewName('default-key');
                }}
                className="px-3 py-1.5 rounded bg-primary-500 hover:bg-primary-600 text-white"
              >
                Create your first access key
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'roles' && (
        <div className="space-y-1">
          {rolesQuery.isLoading && <p className="text-xs text-gray-400">Loading…</p>}
          {rolesQuery.data?.map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1 text-xs">
              <KeyIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-white font-mono flex-1 truncate" title={r.arn}>
                {r.role_name}
              </span>
              <span className="text-gray-500">{r.operations}</span>
              <button
                onClick={() => deleteRole.mutate(r.role_name)}
                className="p-1 text-gray-400 hover:text-red-400"
                title="Delete role"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {rolesQuery.data?.length === 0 && (
            <p className="text-xs text-gray-500 italic">No roles yet</p>
          )}
        </div>
      )}

      {createdKey && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-yellow-500/40 rounded-lg p-5 max-w-xl w-full space-y-3 max-h-[90vh] overflow-y-auto">
            <p className="text-sm text-yellow-300 font-medium">
              Save the secret key now - it will not be shown again.
            </p>
            <div className="space-y-2 text-xs">
              <Field label="Access Key ID" value={createdKey.access_key_id} onCopy={copyToClipboard} />
              <Field label="Secret Access Key" value={createdKey.secret_access_key} onCopy={copyToClipboard} />
              <Field label="Endpoint" value={createdKey.endpoint} onCopy={copyToClipboard} />
              <Field label="Region" value={createdKey.region} onCopy={copyToClipboard} />
              <Field label="Bucket Name" value={createdKey.bucket_key} onCopy={copyToClipboard} />
            </div>
            <details className="text-xs text-gray-300">
              <summary className="cursor-pointer text-primary-300 hover:text-primary-200">
                SDK / CLI snippets (aws-cli, rclone, boto3)
              </summary>
              <div className="mt-2 space-y-2">
                <SnippetBlock
                  title="aws-cli"
                  body={`aws --endpoint-url ${createdKey.endpoint} \\\n    s3 cp ./localfile s3://${createdKey.bucket_key}/path/`}
                  onCopy={copyToClipboard}
                />
                <SnippetBlock
                  title="rclone (~/.config/rclone/rclone.conf)"
                  body={`[podstack]\ntype = s3\nprovider = Wasabi\naccess_key_id = ${createdKey.access_key_id}\nsecret_access_key = ${createdKey.secret_access_key}\nregion = ${createdKey.region}\nendpoint = ${createdKey.endpoint}\n\n# then:\n# rclone sync ./local-dir podstack:${createdKey.bucket_key}/remote-dir --progress`}
                  onCopy={copyToClipboard}
                />
                <SnippetBlock
                  title="boto3"
                  body={`import boto3\ns3 = boto3.client(\n    "s3",\n    endpoint_url="${createdKey.endpoint}",\n    aws_access_key_id="${createdKey.access_key_id}",\n    aws_secret_access_key="${createdKey.secret_access_key}",\n    region_name="${createdKey.region}",\n)\ns3.upload_file("bigfile.tar", "${createdKey.bucket_key}", "remote/bigfile.tar")`}
                  onCopy={copyToClipboard}
                />
              </div>
            </details>
            <button
              onClick={() => setCreatedKey(null)}
              className="w-full px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded"
            >
              I've saved it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onCopy }: { label: string; value: string; onCopy: (s: string) => void }) {
  return (
    <div>
      <p className="text-gray-400">{label}</p>
      <div className="flex gap-2">
        <code className="flex-1 bg-gray-900 p-2 rounded font-mono text-primary-300 break-all">{value}</code>
        <button onClick={() => onCopy(value)} className="px-2 bg-gray-700 hover:bg-gray-600 rounded" title={`Copy ${label}`}>
          <ClipboardDocumentIcon className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}

function SnippetBlock({ title, body, onCopy }: { title: string; body: string; onCopy: (s: string) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-gray-400">{title}</p>
        <button onClick={() => onCopy(body)} className="text-[10px] px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-white rounded">
          Copy
        </button>
      </div>
      <pre className="bg-gray-900 p-2 rounded font-mono text-primary-300 whitespace-pre-wrap break-all text-[11px] leading-snug">
        {body}
      </pre>
    </div>
  );
}
